import loggerService from './logger.service';

import store from '@/store';

export enum ActivityType {
  // Screen Share
  ScreenShareStart = 'screen_share_start',
  ScreenShareStop = 'screen_share_stop',
  ScreenShareStopFailed = 'screen_share_stop_failed',
  ScreenShareFailed = 'screen_share_failed',

  // Video Share
  VideoShareStart = 'video_share_start',
  VideoShareStop = 'video_share_stop',
  VideoShareStopFailed = 'video_share_stop_failed',
  VideoShareFailed = 'video_share_failed',

  // Slide Share
  SlideShareStart = 'slide_share_start',
  SlideShareStop = 'slide_share_stop',
  SlideShareStopFailed = 'slide_share_stop_failed',
  SlideShareFailed = 'slide_share_failed',
  SlideChange = 'slide_change',
  SlideChangeFailed = 'slide_change_failed',

  // Poll
  PollOpen = 'poll_open',
  PollClose = 'poll_close',
  PollShare = 'poll_share',
  PollUnShare = 'poll_unshare',
  PollShareFailed = 'poll_share_failed',
  PollOpenFailed = 'poll_open_failed',
  PollCloseFailed = 'poll_close_failed',
  PollUnShareFailed = 'poll_unshare_failed',

  // Survey
  SurveyOpen = 'survey_open',
  SurveyClose = 'survey_close',
  SurveyOpenFailed = 'survey_open_failed',
  SurveyCloseFailed = 'survey_close_failed',

  // Ticker
  TickerStart = 'ticker_start',
  TickerStop = 'ticker_stop',
  TickerStartFailed = 'ticker_start_failed',
  TickerStopFailed = 'ticker_stop_failed',

  // Logo
  LogoStart = 'logo_start',
  LogoStop = 'logo_stop',
  LogoPositionChange = 'logo_position_change',
  LogoStartFailed = 'logo_start_failed',
  LogoStopFailed = 'logo_stop_failed',
  LogoPositionChangeFailed = 'logo_position_change_failed',

  // Overlay
  OverlayStart = 'overlay_start',
  OverlayStartFailed = 'overlay_start_failed',
  OverlayStop = 'overlay_stop',
  OverlayStopFailed = 'overlay_stop_failed',

  // QnA
  QnAShare = 'qna_share',
  QnAShareFailed = 'qna_share_failed',
  QnAUnShare = 'qna_unshare',
  QnAUnShareFailed = 'qna_unshare_failed',

  // Layout
  LayoutChange = 'layout_change',
  LayoutChangeFailed = 'layout_change_failed',

  //VideoQnA
  RaiseHandAccepted = 'raise_hand_accepted',
  RaiseHandRejected = 'raise_hand_rejected',
  RaiseHandCompleted = 'raise_hand_completed',
  RaiseHandCompletedFailed = 'raise_hand_completed_failed',
  RaiseHandAcceptedFailed = 'raise_hand_accepted_failed',
  RaiseHandRejectedFailed = 'raise_hand_rejected_failed',

  // Speaker Actions
  MoveSpeakerToStage = 'move_speaker_to_stage',
  MoveSpeakerToStageFailed = 'move_speaker_to_stage_failed',
  MoveSpeakerOffStage = 'move_speaker_off_stage',
  MoveSpeakerOffStageFailed = 'move_speaker_off_stage_failed',
  MoveSpeakerToStageCancel = 'move_speaker_to_stage_cancel',
  MoveSpeakerToStageCancelFailed = 'move_speaker_to_stage_cancel_failed',
  MuteSpeaker = 'mute_speaker',
  MuteSpeakerFailed = 'mute_speaker_failed',
  AskToUnmute = 'ask_to_unmute',
  AskToUnmuteFailed = 'ask_to_unmute_failed',
  UnmuteSpeaker = 'unmute_speaker',
  UnmuteSpeakerFailed = 'unmute_speaker_failed',
  ReorderSpeakers = 'reorder_speakers',
  ReorderSpeakersFailed = 'reorder_speakers_failed',

  // Session Start/End
  SessionStart = 'session_start',
  SessionEnd = 'session_end',
  SessionStartFailed = 'session_start_failed',
  SessionEndFailed = 'session_end_failed',

  // Recording Pause/Resume
  RecordingPaused = 'recording_paused',
  RecordingResumed = 'recording_resumed',
  RecordingPausedFailed = 'recording_paused_failed',
  RecordingResumedFailed = 'recording_resumed_failed'
}

export function postSpeakerActivityNotification(activityType: ActivityType, message: string) {
  store.dispatch('event/avroom/postSpeakerActivityNotification', {
    activityType,
    message
  });
}

export async function handleSpeakerActivity<T>(
  action: () => Promise<T>,
  successType: ActivityType,
  failType: ActivityType,
  options?: {
    title?: string;
    speakerName?: string;
    slideNumber?: number;
  }
): Promise<T> {
  const currentUser = store.getters.getCurrentUser;
  const name =
    currentUser?.name ??
    store.state.user.currentEventUser?.first_name + ' ' + store.state.user.currentEventUser?.last_name;

  if (!store.state.event.useSpeakerActivity || store.state.event.event?.event_type === 'Recording') {
    loggerService.warn('Speaker activity is disabled');
    return action();
  }
  try {
    const result = await action();
    postSpeakerActivityNotification(
      successType,
      getSpeakerActivityNotificationMessage(
        successType,
        name,
        options?.title,
        options?.speakerName,
        options?.slideNumber
      )
    );
    return result;
  } catch (error) {
    postSpeakerActivityNotification(
      failType,
      getSpeakerActivityNotificationMessage(failType, name, options?.title, options?.speakerName, options?.slideNumber)
    );
    throw error;
  }
}

export function getSpeakerActivityNotificationMessage(
  activityType: ActivityType,
  userName: string,
  title?: string,
  speakerName?: string,
  slideNumber?: number
) {
  switch (activityType) {
    case ActivityType.ScreenShareStart:
    case ActivityType.ScreenShareStop:
    case ActivityType.ScreenShareFailed:
    case ActivityType.ScreenShareStopFailed:
      return getScreenShareMessage(activityType, userName);

    case ActivityType.VideoShareStart:
    case ActivityType.VideoShareStop:
    case ActivityType.VideoShareFailed:
    case ActivityType.VideoShareStopFailed:
      return getVideoShareMessage(activityType, userName, title);

    case ActivityType.SlideShareStart:
    case ActivityType.SlideShareStop:
    case ActivityType.SlideShareFailed:
    case ActivityType.SlideShareStopFailed:
    case ActivityType.SlideChange:
    case ActivityType.SlideChangeFailed:
      return getSlideShareMessage(activityType, userName, title, slideNumber);

    case ActivityType.PollOpen:
    case ActivityType.PollClose:
    case ActivityType.PollShare:
    case ActivityType.PollShareFailed:
    case ActivityType.PollOpenFailed:
    case ActivityType.PollCloseFailed:
    case ActivityType.PollUnShare:
    case ActivityType.PollUnShareFailed:
      return getPollMessage(activityType, userName);

    case ActivityType.SurveyOpen:
    case ActivityType.SurveyClose:
    case ActivityType.SurveyOpenFailed:
    case ActivityType.SurveyCloseFailed:
      return getSurveyMessage(activityType, userName);

    case ActivityType.TickerStart:
    case ActivityType.TickerStop:
    case ActivityType.TickerStartFailed:
    case ActivityType.TickerStopFailed:
      return getTickerMessage(activityType, userName);

    case ActivityType.LogoStart:
    case ActivityType.LogoStop:
    case ActivityType.LogoPositionChange:
    case ActivityType.LogoStartFailed:
    case ActivityType.LogoStopFailed:
    case ActivityType.LogoPositionChangeFailed:
      return getLogoMessage(activityType, userName, title);

    case ActivityType.OverlayStart:
    case ActivityType.OverlayStop:
    case ActivityType.OverlayStartFailed:
    case ActivityType.OverlayStopFailed:
      return getOverlayMessage(activityType, userName);

    case ActivityType.QnAShare:
    case ActivityType.QnAShareFailed:
    case ActivityType.QnAUnShare:
    case ActivityType.QnAUnShareFailed:
      return getQnAMessage(activityType, userName);

    case ActivityType.LayoutChange:
    case ActivityType.LayoutChangeFailed:
      return getLayoutMessage(activityType, userName, title);

    case ActivityType.RaiseHandAccepted:
    case ActivityType.RaiseHandRejected:
    case ActivityType.RaiseHandCompleted:
    case ActivityType.RaiseHandCompletedFailed:
    case ActivityType.RaiseHandAcceptedFailed:
    case ActivityType.RaiseHandRejectedFailed:
      return getVideoQnAMessage(activityType, userName, speakerName);

    case ActivityType.MoveSpeakerToStage:
    case ActivityType.MoveSpeakerOffStage:
    case ActivityType.MuteSpeaker:
    case ActivityType.MuteSpeakerFailed:
    case ActivityType.AskToUnmute:
    case ActivityType.AskToUnmuteFailed:
    case ActivityType.UnmuteSpeaker:
    case ActivityType.UnmuteSpeakerFailed:
    case ActivityType.MoveSpeakerToStageFailed:
    case ActivityType.MoveSpeakerOffStageFailed:
    case ActivityType.MoveSpeakerToStageCancel:
    case ActivityType.MoveSpeakerToStageCancelFailed:
    case ActivityType.ReorderSpeakers:
    case ActivityType.ReorderSpeakersFailed:
      return getSpeakerMessage(activityType, userName, speakerName);

    case ActivityType.SessionStart:
    case ActivityType.SessionEnd:
    case ActivityType.SessionStartFailed:
    case ActivityType.SessionEndFailed:
      return getSessionMessage(activityType, userName);

    case ActivityType.RecordingPaused:
    case ActivityType.RecordingResumed:
    case ActivityType.RecordingPausedFailed:
    case ActivityType.RecordingResumedFailed:
      return getRecordingMessage(activityType, userName);

    default:
      return '';
  }
}

const getScreenShareMessage = (activityType: ActivityType, userName: string) => {
  switch (activityType) {
    case ActivityType.ScreenShareStart:
      return `${userName} started sharing their screen`;
    case ActivityType.ScreenShareStop:
      return `${userName} stopped sharing their screen`;
    case ActivityType.ScreenShareFailed:
      return `${userName} failed to share their screen`;
    case ActivityType.ScreenShareStopFailed:
      return `${userName} failed to stop sharing their screen`;
    default:
      return '';
  }
};

const getVideoShareMessage = (activityType: ActivityType, userName: string, title?: string) => {
  switch (activityType) {
    case ActivityType.VideoShareStart:
      return title ? ` ${userName} started sharing video: ${title}` : ` ${userName} started sharing video`;
    case ActivityType.VideoShareStop:
      return title ? `${userName} stopped sharing video: ${title}` : `${userName} stopped sharing video`;
    case ActivityType.VideoShareFailed:
      return `${userName} failed to share video`;
    case ActivityType.VideoShareStopFailed:
      return `${userName} failed to stop sharing video`;
    default:
      return '';
  }
};

const getSlideShareMessage = (activityType: ActivityType, userName: string, title?: string, slideNumber?: number) => {
  switch (activityType) {
    case ActivityType.SlideShareStart:
      return `${userName} started sharing slide ${title}`;
    case ActivityType.SlideShareStop:
      return `${userName} stopped sharing slide ${title}`;
    case ActivityType.SlideChange:
      return slideNumber !== undefined
        ? `${userName} changed to slide ${slideNumber + 1}`
        : `${userName} changed slides`;
    case ActivityType.SlideShareFailed:
      return `${userName} failed to share slide ${title}`;
    case ActivityType.SlideChangeFailed:
      return `${userName} failed to change slides`;
    case ActivityType.SlideShareStopFailed:
      return `${userName} failed to stop sharing slide`;
    default:
      return '';
  }
};

const getPollMessage = (activityType: ActivityType, userName: string) => {
  switch (activityType) {
    case ActivityType.PollOpen:
      return `${userName} opened a poll`;
    case ActivityType.PollClose:
      return `${userName} closed the poll`;
    case ActivityType.PollShare:
      return `${userName} shared poll results`;
    case ActivityType.PollOpenFailed:
      return `${userName} failed to open poll`;
    case ActivityType.PollCloseFailed:
      return `${userName} failed to close poll`;
    case ActivityType.PollShareFailed:
      return `${userName} failed to share poll results`;
    case ActivityType.PollUnShare:
      return `${userName} unshared poll results`;
    case ActivityType.PollUnShareFailed:
      return `${userName} failed to unshare poll results`;
    default:
      return '';
  }
};

const getSurveyMessage = (activityType: ActivityType, userName: string) => {
  switch (activityType) {
    case ActivityType.SurveyOpen:
      return `${userName} opened a survey`;
    case ActivityType.SurveyClose:
      return `${userName} closed the survey`;
    case ActivityType.SurveyOpenFailed:
      return `${userName} failed to open survey`;
    case ActivityType.SurveyCloseFailed:
      return `${userName} failed to close survey`;
    default:
      return '';
  }
};

const getTickerMessage = (activityType: ActivityType, userName: string) => {
  switch (activityType) {
    case ActivityType.TickerStart:
      return `${userName} started a ticker`;
    case ActivityType.TickerStop:
      return `${userName} stopped the ticker`;
    case ActivityType.TickerStartFailed:
      return `${userName} failed to start ticker`;
    case ActivityType.TickerStopFailed:
      return `${userName} failed to stop ticker`;
    default:
      return '';
  }
};

const getLogoMessage = (activityType: ActivityType, userName: string, title?: string) => {
  switch (activityType) {
    case ActivityType.LogoStart:
      return `${userName} started sharing logo`;
    case ActivityType.LogoStop:
      return `${userName} stopped sharing logo`;
    case ActivityType.LogoPositionChange:
      return `${userName} changed the logo position to ${title}`;
    case ActivityType.LogoStartFailed:
      return `${userName} failed to start sharing logo`;
    case ActivityType.LogoStopFailed:
      return `${userName} failed to stop sharing logo`;
    case ActivityType.LogoPositionChangeFailed:
      return `${userName} failed to change logo position to ${title}`;
    default:
      return '';
  }
};

const getOverlayMessage = (activityType: ActivityType, userName: string) => {
  switch (activityType) {
    case ActivityType.OverlayStart:
      return `${userName} started an overlay`;
    case ActivityType.OverlayStop:
      return `${userName} stopped the overlay`;
    case ActivityType.OverlayStartFailed:
      return `${userName} failed to start overlay`;
    case ActivityType.OverlayStopFailed:
      return `${userName} failed to stop overlay`;
    default:
      return '';
  }
};

const getQnAMessage = (activityType: ActivityType, userName: string) => {
  switch (activityType) {
    case ActivityType.QnAShare:
      return `${userName} shared a QnA`;
    case ActivityType.QnAShareFailed:
      return `${userName} failed to share QnA`;
    case ActivityType.QnAUnShare:
      return `${userName} unshared QnA`;
    case ActivityType.QnAUnShareFailed:
      return `${userName} failed to unshare QnA`;
    default:
      return '';
  }
};

const getLayoutMessage = (activityType: ActivityType, userName: string, title?: string) => {
  switch (activityType) {
    case ActivityType.LayoutChange:
      return `${userName} changed the layout to ${title}`;
    case ActivityType.LayoutChangeFailed:
      return `${userName} failed to change the layout to ${title}`;
    default:
      return '';
  }
};

const getSpeakerMessage = (activityType: ActivityType, userName: string, speakerName?: string) => {
  switch (activityType) {
    case ActivityType.MoveSpeakerToStage:
      return `${userName} moved ${speakerName} to the stage`;
    case ActivityType.MoveSpeakerOffStage:
      return `${userName} moved ${speakerName} off stage`;
    case ActivityType.MuteSpeaker:
      return `${userName} muted ${speakerName}`;
    case ActivityType.AskToUnmute:
      return `${userName} asked to unmute ${speakerName}`;
    case ActivityType.AskToUnmuteFailed:
      return `${userName} failed to ask to unmute ${speakerName}`;
    case ActivityType.UnmuteSpeaker:
      return `${userName} unmuted ${speakerName}`;
    case ActivityType.UnmuteSpeakerFailed:
      return `${userName} failed to unmute ${speakerName}`;
    case ActivityType.MoveSpeakerToStageFailed:
      return `${userName} failed to move ${speakerName} to the stage`;
    case ActivityType.MoveSpeakerOffStageFailed:
      return `${userName} failed to move ${speakerName} off stage`;
    case ActivityType.MuteSpeakerFailed:
      return `${userName} failed to mute ${speakerName}`;
    case ActivityType.MoveSpeakerToStageCancel:
      return `${userName} cancelled the move to stage request for ${speakerName}`;
    case ActivityType.MoveSpeakerToStageCancelFailed:
      return `${userName} failed to cancel the move to stage request for ${speakerName}`;
    case ActivityType.ReorderSpeakers:
      return `${userName} reordered speakers`;
    case ActivityType.ReorderSpeakersFailed:
      return `${userName} failed to reorder speakers`;
    default:
      return '';
  }
};

const getVideoQnAMessage = (activityType: ActivityType, userName: string, speakerName?: string) => {
  switch (activityType) {
    case ActivityType.RaiseHandAccepted:
      return `${userName} accepted a raise hand`;
    case ActivityType.RaiseHandRejected:
      return `${userName} rejected a raise hand`;
    case ActivityType.RaiseHandCompleted:
      return `${userName} moved user off stage`;
    case ActivityType.RaiseHandCompletedFailed:
      return `${userName} failed to move user off stage`;
    case ActivityType.RaiseHandAcceptedFailed:
      return `${userName} failed to accept a raise hand`;
    case ActivityType.RaiseHandRejectedFailed:
      return `${userName} failed to reject a raise hand`;
    default:
      return '';
  }
};

const getSessionMessage = (activityType: ActivityType, userName: string) => {
  switch (activityType) {
    case ActivityType.SessionStart:
      return `${userName} started the session`;
    case ActivityType.SessionEnd:
      return `${userName} ended the session`;
    case ActivityType.SessionStartFailed:
      return `${userName} failed to start the session`;
    case ActivityType.SessionEndFailed:
      return `${userName} failed to end the session`;
    default:
      return '';
  }
};

const getRecordingMessage = (activityType: ActivityType, userName: string) => {
  switch (activityType) {
    case ActivityType.RecordingPaused:
      return `${userName} paused the recording`;
    case ActivityType.RecordingResumed:
      return `${userName} resumed the recording`;
    case ActivityType.RecordingPausedFailed:
      return `${userName} failed to pause the recording`;
    case ActivityType.RecordingResumedFailed:
      return `${userName} failed to resume the recording`;
    default:
      return '';
  }
};
